import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { getKpiUsers } from "api/workers";

export const ROLES = { ADMIN: "admin", SUPERVISOR: "supervisor", OPERATOR: "operator" };
export const ROLE_LABELS = {
  [ROLES.ADMIN]: "Admin",
  [ROLES.SUPERVISOR]: "Supervisor",
  [ROLES.OPERATOR]: "Operator",
};

function mapApiUserToWorker(apiUser) {
  return {
    id: apiUser.id,
    name: apiUser.name || "",
    email: apiUser.email || "",
    employeeId: apiUser.employee_id || "",
    role: apiUser.role || ROLES.OPERATOR,
    sector_name: apiUser.sector_name || null,
    created_at: apiUser.created_at,
    updated_at: apiUser.updated_at,
  };
}

const WorkersContext = createContext(null);

export function WorkersProvider({ children }) {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWorkers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getKpiUsers();
      const list = (result.data || []).map(mapApiUserToWorker);
      setWorkers(list);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load workers");
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const addWorker = useCallback((worker) => {
    const id = worker.id || `w-${Date.now()}`;
    setWorkers((prev) => [...prev, { ...worker, id }]);
    return id;
  }, []);

  const updateWorker = useCallback((id, updates) => {
    setWorkers((prev) =>
      prev.map((w) => (w.id === id ? { ...w, ...updates } : w))
    );
  }, []);

  const removeWorker = useCallback((id) => {
    setWorkers((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const workersByRole = useMemo(() => ({
    [ROLES.ADMIN]: workers.filter((w) => w.role === ROLES.ADMIN),
    [ROLES.SUPERVISOR]: workers.filter((w) => w.role === ROLES.SUPERVISOR),
    [ROLES.OPERATOR]: workers.filter((w) => w.role === ROLES.OPERATOR),
  }), [workers]);

  const supervisorsForAssignment = useMemo(
    () =>
      workers
        .filter((w) => w.role === ROLES.SUPERVISOR)
        .map((w) => ({ id: w.id, name: w.name, badge: w.employeeId || w.id })),
    [workers]
  );

  const operatorsForAssignment = useMemo(
    () =>
      workers
        .filter((w) => w.role === ROLES.OPERATOR)
        .map((w) => ({ id: w.id, name: w.name })),
    [workers]
  );

  const value = useMemo(
    () => ({
      workers,
      workersByRole,
      addWorker,
      updateWorker,
      removeWorker,
      supervisorsForAssignment,
      operatorsForAssignment,
      loading,
      error,
      refetchWorkers: fetchWorkers,
    }),
    [
      workers,
      workersByRole,
      addWorker,
      updateWorker,
      removeWorker,
      supervisorsForAssignment,
      operatorsForAssignment,
      loading,
      error,
      fetchWorkers,
    ]
  );

  return (
    <WorkersContext.Provider value={value}>{children}</WorkersContext.Provider>
  );
}

export function useWorkers() {
  const ctx = useContext(WorkersContext);
  if (!ctx) {
    throw new Error("useWorkers must be used within WorkersProvider");
  }
  return ctx;
}

export default WorkersContext;
